// leaseholderRemovalClient — AUTHENTICATED staff client for the Leaseholder
// Removal & Release Agreement send surface (/send/leaseholder-removal).
//
// The remove-only sibling of the leaseholder-change agreement. There is NO
// application picker and NO /candidates endpoint — initiation is TENANCY-FIRST
// (staff pick a property + unit). Two endpoints, both Bearer-authed via the
// shared `api` helper (wire shapes mirror apps/api/src/routes/leaseholderRemoval.ts
// — keep in sync):
//
//   • GET  /api/leaseholder-removal/context?propertyId=<id>&unitId=<id>
//       → LeaseholderRemovalContext
//     The complete working set for one tenancy: the financially-responsible
//     residents currently on the lease, the resolved lease start date, and the
//     premises address. `sendEnabled` reports the LEASEHOLDER_REMOVAL_SEND_ENABLED
//     gate so the page can flag the dormant state up front.
//
//   • POST /api/leaseholder-removal/initiate → InitiateLeaseholderRemovalResult
//     Renders the fully-baked agreement, creates the DocuSeal submission, and
//     emails every resident-group signer their /sign/:uuid link. Errors:
//       400 invalid input / duplicate signer email → { error, detail: string[] }
//       409 missing signer email(s)                → { error, detail: string[] }
//       503 feature dormant → LeaseholderRemovalNotEnabledError

import { api, ApiError } from './api';

// ---------------------------------------------------------------------------
// Wire types (mirror apps/api)
// ---------------------------------------------------------------------------

/** One financially-responsible resident currently on the lease. */
export interface LeaseholderRemovalResident {
  /** AppFolio tenant id (stable row key). */
  tenantId: string;
  firstName: string;
  lastName: string;
  email: string | null;
}

export interface LeaseholderRemovalContext {
  residents: LeaseholderRemovalResident[];
  /** yyyy-mm-dd, or '' when AppFolio carries none on file. */
  leaseStartDate: string;
  propertyAddress: string;
  /** Whether LEASEHOLDER_REMOVAL_SEND_ENABLED (+ WHP_OWN_MESSAGING) is on. */
  sendEnabled: boolean;
}

/** One party on the initiate body (remaining / outgoing). */
export interface LeaseholderRemovalPerson {
  firstName: string;
  lastName: string;
  email: string;
}

export interface InitiateLeaseholderRemovalBody {
  propertyId: string;
  unitId: string;
  propertyAddress: string;
  /** yyyy-mm-dd — the date the lease was signed (staff-confirmed). */
  originalLeaseDate: string;
  /** yyyy-mm-dd — the removal takes effect at 11:59 p.m. on this date. */
  effectiveDate: string;
  /** The residents who STAY on the lease (>= 1). */
  remaining: LeaseholderRemovalPerson[];
  /** The residents being REMOVED and released (>= 1). */
  outgoing: LeaseholderRemovalPerson[];
}

/** One signer on the created submission, echoed back by `initiate`. */
export interface LeaseholderRemovalSubmitter {
  /** RemainingResident1..N / OutgoingResident1..N / Lessor. */
  role: string;
  name?: string;
  email?: string;
  /** 0 = resident groups (parallel); 1 = Lessor (countersigns last). */
  order?: number;
  /** pending / sent / opened / completed / declined. */
  status: string;
  sentAt?: string;
}

export interface InitiateLeaseholderRemovalResult {
  /** The synthetic `leaseholder-removal-<ulid>` group id. */
  groupId: string;
  docusealSubmissionId: number;
  status: string;
  submitters: LeaseholderRemovalSubmitter[];
  /** Resident-group signers emailed their signing link this run. */
  emailed: number;
}

// ---------------------------------------------------------------------------
// Errors
// ---------------------------------------------------------------------------

/**
 * Thrown when the initiate route answers 503 — the feature is gated off
 * (LEASEHOLDER_REMOVAL_SEND_ENABLED / WHP_OWN_MESSAGING). The page catches this
 * to render the dormant state (mirrors leaseholderChangeClient's NotEnabledError).
 */
export class LeaseholderRemovalNotEnabledError extends Error {
  constructor(message = 'Leaseholder removal sends are not enabled yet.') {
    super(message);
    this.name = 'LeaseholderRemovalNotEnabledError';
  }
}

/** Read the `{ error, detail }` body off a 400/409 initiate rejection. */
export function leaseholderRemovalErrorDetail(err: unknown): {
  message: string;
  detail: string[];
} | null {
  if (!(err instanceof ApiError)) return null;
  const body = err.body as { error?: string; detail?: unknown } | null;
  return {
    message: body?.error ?? `Could not send the agreement (HTTP ${err.status})`,
    detail: Array.isArray(body?.detail) ? (body.detail as string[]) : [],
  };
}

// ---------------------------------------------------------------------------
// Calls
// ---------------------------------------------------------------------------

/** Load the builder's working set for one tenancy by property/unit. */
export function getLeaseholderRemovalContext(
  propertyId: string,
  unitId: string,
): Promise<LeaseholderRemovalContext> {
  const qs = `propertyId=${encodeURIComponent(propertyId)}&unitId=${encodeURIComponent(unitId)}`;
  return api
    .get<Partial<LeaseholderRemovalContext>>(`/api/leaseholder-removal/context?${qs}`)
    .then((raw) => ({
      residents: raw.residents ?? [],
      leaseStartDate: raw.leaseStartDate ?? '',
      propertyAddress: raw.propertyAddress ?? '',
      sendEnabled: raw.sendEnabled ?? false,
    }));
}

/**
 * Render + send the agreement to every party. Throws
 * LeaseholderRemovalNotEnabledError on a 503 (feature dormant); 400/409
 * propagate as ApiError with `{ error, detail }` (read via
 * leaseholderRemovalErrorDetail).
 */
export async function initiateLeaseholderRemoval(
  body: InitiateLeaseholderRemovalBody,
): Promise<InitiateLeaseholderRemovalResult> {
  try {
    const raw = await api.post<{
      groupId: string;
      docusealSubmissionId?: number;
      status?: string;
      submitters?: LeaseholderRemovalSubmitter[];
      emailed?: number;
    }>('/api/leaseholder-removal/initiate', body);
    return {
      groupId: raw.groupId,
      docusealSubmissionId: raw.docusealSubmissionId ?? 0,
      status: raw.status ?? '',
      submitters: raw.submitters ?? [],
      emailed: raw.emailed ?? 0,
    };
  } catch (err) {
    if (err instanceof ApiError && err.status === 503) {
      throw new LeaseholderRemovalNotEnabledError(
        (err.body as { error?: string } | null)?.error ??
          'Leaseholder removal sends are not enabled yet.',
      );
    }
    throw err;
  }
}
